import React, { useEffect, useState } from "react";
import { listAllAppointments } from "../../api/Scheduling/appointments";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { loadPatientName } from "./names";
import { subjectPatientRef } from "./appointments/statusLabels";
import { formatRange } from "./time";
import { Button } from "../../common/Button";

export default function BlockedAppointments({ block, organizationRef, timezoneName, onOpenAppointment, scope, excludedOrganizationRefs }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const exclusionsKey = JSON.stringify(excludedOrganizationRefs || []);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setRows([]);
    listAllAppointments({ organizationRef, scope, excludedOrganizationRefs: JSON.parse(exclusionsKey), startsAt: block.startsAt, endsAt: block.endsAt,
      ...(block.practitionerRef ? { practitionerRef: block.practitionerRef } : { scheduleId: block.scheduleId }),
      consistency: "strong", signal: controller.signal }).then(appointments => {
      if (!active) return;
      const overlapping = appointments.filter(item => ["proposed", "booked"].includes(item.status)
        && new Date(item.startsAt) < new Date(block.endsAt) && new Date(item.endsAt) > new Date(block.startsAt))
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
      setRows(overlapping.map(appointment => ({ appointment, name: "Appointment" })));
      setLoading(false);
      Promise.all(overlapping.map(async appointment => ({ appointment,
        name: await loadPatientName(subjectPatientRef(appointment)) || "Appointment" }))).then(result => {
        if (active) setRows(result);
      }).catch(() => {});
    }).catch(cause => {
      if (active) setError(schedulingErrorMessage(cause, "Could not load appointments."));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [organizationRef, scope, exclusionsKey, block.startsAt, block.endsAt, block.practitionerRef, block.scheduleId, retry]);

  return <section className="scheduling-block-appointments" aria-label="Appointments to reschedule">
    <h3>{scope === "descendants" ? "Appointments to reschedule" : "Appointments in this organization"}</h3>
    {scope === "descendants" && <p className="schedule-help">In the organizations you can access.</p>}
    {loading ? <p role="status">Loading appointments…</p> : error ? <>
      <p role="alert">{error}</p><Button variant="secondary" onClick={() => setRetry(value => value + 1)}>Retry</Button>
    </> : rows.length ? <>
      <p>{rows.length} appointment{rows.length === 1 ? "" : "s"} in blocked time.</p>
      <ul>{rows.map(({ appointment, name }) => <li key={appointment.appointmentId}>
        <button type="button" onClick={() => onOpenAppointment(appointment)}
          aria-label={`Review ${name}, ${formatRange(appointment.startsAt, appointment.endsAt, timezoneName)}`}>
          <strong>{name}</strong><span>{formatRange(appointment.startsAt, appointment.endsAt, timezoneName)}</span>
          <span>Review appointment</span>
        </button>
      </li>)}</ul>
    </> : <p>{scope === "descendants" ? "No active appointments in the organizations you can access during the block." : "No active appointments in this organization during the block."}</p>}
  </section>;
}
